import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import { searchDatasetsAlgolia } from "@/lib/algoliaSearch";

/**
 * The dataset pages that answer the same query, above the job results.
 *
 * Someone searching "controller denver" wants the openings and what Denver
 * pays a controller. We hold both, but until the dataset pages entered the
 * search index only the openings were reachable: "layoffs", "public pay
 * ontario" and "cpa exam support" each returned zero hits.
 *
 * Deliberately a thin strip rather than a card block. It sits above results
 * the reader asked for, so it has to earn its space in one line.
 */
export default function DataAnswers({ query }: { query: string }) {
  const q = query.trim();
  const { data } = useQuery({
    queryKey: ["datasetSuggestions", q],
    queryFn: () => searchDatasetsAlgolia(q, 3),
    enabled: q.length >= 3,
    staleTime: 5 * 60 * 1000,
  });

  if (!data?.length) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-input bg-muted/40 px-4 py-3">
      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <BarChart3 className="h-3.5 w-3.5" aria-hidden="true" />
        Our data on this
      </span>
      {data.map((d) => (
        <Link
          key={d.objectID}
          // Index records carry absolute URLs so they work in every surface;
          // inside the SPA the path alone avoids a full page load.
          to={new URL(d.url).pathname}
          className="text-sm font-semibold text-foreground underline-offset-4 hover:underline"
          title={d.tagline ?? undefined}
        >
          {d.name}
        </Link>
      ))}
    </div>
  );
}
